import { useCallback, useRef } from "react";
import { v4 as uuid } from "uuid";

import { usePromise } from "@touchtech/web-hooks";

import { PackType } from "@is/constants";
import { useConfiguration } from "@is/hooks/useConfiguration";
import { createWidgetUrl } from "@is/services/api";
import {
  HTWIMedia,
  HowToWearItItem,
  HowToWearItWidget,
  MediaOrderingRule,
  ProductItem,
  SearchTerm,
  WidgetType,
} from "@is/types";
import { fetchData, getHTWISortedMedia, getMainImageUrl } from "@is/utils";

type Response = {
  items: ProductItem[];
};

type HTWIResponse = {
  items: HowToWearItItem[];
};

const PAGE_SIZE = 24;

type State = {
  isPending: boolean;
  error: Error | null;
  items?: ProductItem[];
};

type Params = {
  type: WidgetType;
  variantId?: string;
  styleId?: string;
  variants?: Array<{ variantId: string; styleId: string }>;
  ean?: string;
  filters?: SearchTerm[];
};

async function handleGetWidget(
  params: Params,
  controllerRef: React.MutableRefObject<AbortController | null>
): Promise<ProductItem[]> {
  const { type, variantId, styleId, variants, ean, filters } = params;

  if (controllerRef?.current) {
    controllerRef?.current?.abort();
  }

  controllerRef.current = new AbortController();

  const body = {
    ...(variants
      ? { variants }
      : {
          variant: {
            variantId,
            styleId,
          },
        }),
    pageSize: PAGE_SIZE,
    page: 1,
    ...(ean && { ean }),
    ...(filters && { filters }),
  };

  const response = await fetchData(
    createWidgetUrl(type),
    body,
    "POST",
    controllerRef
  );

  const widgetResponse = (await response.json()) as Response;

  return widgetResponse.items;
}

async function handleGetHTWIWidget(
  variantId: string,
  styleId: string,
  filters: SearchTerm[],
  controllerRef: React.MutableRefObject<AbortController | null>,
  useAbortController = true
): Promise<HowToWearItWidget[]> {
  if (useAbortController && controllerRef?.current) {
    controllerRef?.current?.abort();
  }

  if (useAbortController) {
    controllerRef.current = new AbortController();
  }

  const body = {
    page: 1,
    pageSize: PAGE_SIZE,
    filters,
    variants: [{ variantId, styleId }],
  };

  const response = await fetchData(
    createWidgetUrl(WidgetType.HowToWearIt),
    body,
    "POST",
    useAbortController ? controllerRef : null
  );

  const HTWIResponse = (await response.json()) as HTWIResponse;

  const HTWIData = HTWIResponse.items;

  const howToWearItWidgets: HowToWearItWidget[] = [];

  for (const HTWIItem of HTWIData) {
    const media: HTWIMedia[] = [];

    for (const item of HTWIItem.items) {
      const medias = item.media ?? [];
      const orderRule = MediaOrderingRule.ColorThumbnails;

      const url = getMainImageUrl(medias, orderRule);

      media.push({
        id: uuid(),
        variantId: item.id,
        styleId: item.styleId,
        packType: item.packType,
        url,
      });
    }

    let widgetMedia = getHTWISortedMedia(media);

    if (HTWIItem.modelShotUrl) {
      const insertIndex =
        widgetMedia.length === 1 ? 0 : Math.ceil(widgetMedia.length / 2);

      const left = widgetMedia.slice(0, insertIndex);
      const right = widgetMedia.slice(insertIndex);

      widgetMedia = [
        ...left,
        {
          id: uuid(),
          variantId: "",
          styleId: "",
          packType: PackType.ModelShot,
          url: HTWIItem.modelShotUrl,
        },
        ...right,
      ];
    }

    howToWearItWidgets.push({
      ...HTWIItem,
      media: widgetMedia,
      mainStyle: HTWIItem.items.find((item) => item.id === variantId) || null,
    });
  }

  return howToWearItWidgets;
}

export const useWidget = (): [
  State,
  (params: Params) => Promise<ProductItem[]>,
  (
    variantId: string,
    styleId: string,
    filters: SearchTerm[],
    useAbortController?: boolean
  ) => Promise<HowToWearItWidget[]>
] => {
  const [{ isPending, error, data }, dispatch] = usePromise<ProductItem[]>();

  const { productDetailsView, basketAiSuggestions } = useConfiguration();

  const howToWearItControllerRef = useRef(null);
  const similarProductsControllerRef = useRef(null);
  const howAboutTheseControllerRef = useRef(null);

  const getWidget = useCallback(
    (params: Params) => {
      if (
        (params.type === WidgetType.SimilarProducts &&
          !productDetailsView?.enableYouMayAlsoLike) ||
        (params.type === WidgetType.HowAboutThese &&
          !basketAiSuggestions?.enableHowAboutThese)
      ) {
        return;
      }

      let controller;

      switch (params.type) {
        case WidgetType.SimilarProducts:
          controller = similarProductsControllerRef;
          break;
        case WidgetType.HowAboutThese:
          controller = howAboutTheseControllerRef;
          break;
      }

      return dispatch(handleGetWidget(params, controller));
    },
    [
      dispatch,
      productDetailsView,
      basketAiSuggestions,
      similarProductsControllerRef,
      howAboutTheseControllerRef,
    ]
  );

  const getHTWIWidget = useCallback(
    (
      variantId: string,
      styleId: string,
      filters: SearchTerm[],
      useAbortController = true
    ) => {
      if (!productDetailsView?.enableHowToWearIt) {
        return;
      }

      return handleGetHTWIWidget(
        variantId,
        styleId,
        filters,
        howToWearItControllerRef,
        useAbortController
      );
    },
    [productDetailsView, howToWearItControllerRef]
  );

  return [{ items: data || [], isPending, error }, getWidget, getHTWIWidget];
};
